import React, { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Check, ChevronRight, Loader2, Sparkles } from "lucide-react";
import { ScreenHeader } from "./MobileShell";
import { PILLARS } from "./data/pillars";
import { listPlanCompletions, togglePlanCompletion } from "../lib/api";

export function todayISO() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

const CELEBRATED_KEY = "lc_m_celebrated_v1";

export default function MyPlanScreen() {
  const { token } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [completed, setCompleted] = useState({}); // {activity_id: true}
  const [busy, setBusy] = useState(null);
  const [celebrate, setCelebrate] = useState(false);

  const date = todayISO();

  const load = async () => {
    try {
      const rows = await listPlanCompletions(token, date);
      const map = {};
      rows.forEach((r) => {
        map[r.activity_id] = true;
      });
      setCompleted(map);
    } catch {
      /* ignore — show nothing completed */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const refs = useRef({});
  useEffect(() => {
    const hash = window.location.hash?.slice(1);
    if (hash && refs.current[hash]) {
      refs.current[hash].scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [loading]);

  const toggleItem = async (activityId) => {
    if (busy) return;
    const nextVal = !completed[activityId];
    // Optimistic flip
    setBusy(activityId);
    setCompleted((c) => ({ ...c, [activityId]: nextVal }));
    try {
      await togglePlanCompletion(token, {
        activity_id: activityId,
        date,
        completed: nextVal,
      });
      // Check if all 6 pillars are now touched → one-time local celebration
      if (nextVal) {
        const nextMap = { ...completed, [activityId]: true };
        const pillarsHit = new Set();
        Object.keys(nextMap).forEach((id) => {
          if (nextMap[id]) pillarsHit.add(id.split(".")[0]);
        });
        const allSix = PILLARS.every((p) => pillarsHit.has(p.id));
        const key = `${CELEBRATED_KEY}:${date}`;
        if (allSix && !sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          setCelebrate(true);
          setTimeout(() => setCelebrate(false), 4200);
        }
      }
    } catch {
      // Rollback on failure
      setCompleted((c) => ({ ...c, [activityId]: !nextVal }));
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <ScreenHeader eyebrow="Your longevity plan" title="Today's priorities" />

      <p className="text-xs text-[var(--lc-muted)] mb-5">
        Tap an activity to mark it done. Your streak syncs across every device.
      </p>

      {loading ? (
        <div className="py-10 grid place-items-center" data-testid="plan-loading">
          <Loader2 className="h-5 w-5 animate-spin text-[var(--lc-muted)]" />
        </div>
      ) : (
        <div className="space-y-5" data-testid="plan-list">
          {PILLARS.map((p) => {
            const ids = p.activities.map((a) => a.id);
            const done = ids.filter((id) => completed[id]).length;
            return (
              <section
                key={p.id}
                ref={(el) => (refs.current[p.id] = el)}
                className="scroll-mt-6"
                data-testid={`plan-pillar-${p.id}`}
              >
                <header className="flex items-center justify-between mb-2.5">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className={`w-9 h-9 rounded-md border grid place-items-center ${p.tint}`}>
                      <p.icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-serif-display text-lg leading-tight truncate">{p.name}</div>
                      <div className="eyebrow text-[var(--lc-muted)] mt-0.5">
                        {done}/{ids.length} done today
                      </div>
                    </div>
                  </div>
                  <div className="text-[11px] text-[var(--lc-moss-dark)] eyebrow">
                    {Math.round((done / ids.length) * 100)}%
                  </div>
                </header>

                <ul className="bg-white border border-[var(--lc-line)] rounded-xl divide-y divide-[var(--lc-line)] overflow-hidden">
                  {p.activities.map((a) => {
                    const checked = !!completed[a.id];
                    const status = checked
                      ? "Completed"
                      : done === 0 && a === p.activities[0]
                      ? "Not started"
                      : "In progress";
                    return (
                      <li key={a.id} data-testid={`plan-item-${a.id}`}>
                        <button
                          type="button"
                          onClick={() => toggleItem(a.id)}
                          aria-pressed={checked}
                          disabled={busy === a.id}
                          className="w-full flex items-center gap-3 py-3 px-4 text-left disabled:opacity-70"
                          data-testid={`plan-item-toggle-${a.id}`}
                        >
                          <span
                            className={`h-6 w-6 rounded-full border-2 grid place-items-center flex-shrink-0 transition-colors ${
                              checked
                                ? "bg-[var(--lc-moss)] border-[var(--lc-moss)] text-white"
                                : "border-[var(--lc-line)] bg-white"
                            }`}
                          >
                            {busy === a.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : checked ? (
                              <Check className="h-3.5 w-3.5" strokeWidth={3} />
                            ) : null}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span
                              className={`block text-sm font-medium leading-snug ${
                                checked ? "line-through text-[var(--lc-muted)]" : ""
                              }`}
                            >
                              {a.name}
                            </span>
                            <span className="block text-[10px] uppercase tracking-[0.14em] text-[var(--lc-muted)] mt-1">
                              {a.frequency} · {status}
                            </span>
                          </span>
                          <ChevronRight className="h-4 w-4 text-[var(--lc-muted)]" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      )}

      <p className="mt-6 text-[10px] text-center text-[var(--lc-muted)] tracking-[0.14em] uppercase">
        {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
      </p>

      {celebrate && (
        <div
          className="fixed left-3 right-3 z-50 bg-[var(--lc-ink)] text-[var(--lc-cream)] rounded-xl p-4 shadow-2xl flex items-start gap-3 animate-in slide-in-from-bottom-5"
          style={{ bottom: "calc(96px + env(safe-area-inset-bottom))" }}
          data-testid="plan-celebration-toast"
          role="status"
          aria-live="polite"
        >
          <div className="w-9 h-9 rounded-full bg-[var(--lc-moss)] grid place-items-center flex-shrink-0">
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-serif-display text-base leading-tight">You closed the circle 🌿</div>
            <div className="text-[11px] text-[var(--lc-cream)]/70 mt-0.5 leading-snug">
              All six pillars touched today — the streak lives on. Beautiful work.
            </div>
          </div>
          <button
            onClick={() => setCelebrate(false)}
            className="text-[var(--lc-cream)]/60 text-[11px] eyebrow"
            aria-label="Dismiss"
          >
            Dismiss
          </button>
        </div>
      )}
    </>
  );
}
